import { useState, useMemo, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown, Users, Plus, X, Download, FileText, Share2, Pencil, Mail, Loader2 } from 'lucide-react';
import './WorkerCalendar.css';
import WhatsAppBulk from './WhatsAppBulk';
import SearchableSelect from './SearchableSelect';
import { fold } from '../../utils/zoek';

function authFetch(url, options = {}) {
  const token = localStorage.getItem('ozel_token');
  return fetch(url, {
    ...options,
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    credentials: 'include',
  });
}

// Blob (PNG) -> bare base64 (no data: prefix).
const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onloadend = () => resolve(String(r.result).split(',')[1]);
  r.onerror = reject;
  r.readAsDataURL(blob);
});

// Map a worker's opdrachtgever (Özel/Myform/Entice) to the mail profile/domain.
const opdrachtgeverToProfiel = (name) => {
  const n = (name || '').toLowerCase();
  if (n.includes('myform')) return 'myform';
  if (n.includes('entice')) return 'entice';
  return 'ozel'; // özel/ozel or unknown -> ozel
};

function getWeekNumber(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
}

function getMondayOfWeek(year, week) {
  const jan4 = new Date(year, 0, 4);
  const dayOfWeek = jan4.getDay() || 7;
  const monday = new Date(jan4);
  monday.setDate(jan4.getDate() - dayOfWeek + 1 + (week - 1) * 7);
  return monday;
}

function getWeekDaysMondayFirst(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d.setDate(diff));
  const days = [];
  for (let i = 0; i < 7; i++) {
    const nd = new Date(monday);
    nd.setDate(monday.getDate() + i);
    days.push(nd);
  }
  return days;
}

function toDateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const DAY_ABBR_NL = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];
const DAY_FULL_NL = ['maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag', 'zondag'];
const MONTH_NAMES_NL = [
  'januari', 'februari', 'maart', 'april', 'mei', 'juni',
  'juli', 'augustus', 'september', 'oktober', 'november', 'december',
];

// Nieuwe weergave (/nieuw): vaste kleur per klant en initialen voor de avatar.
const BLOCK_COLORS = ['#e65100', '#1f6fb2', '#2e7d32', '#7b3fa0', '#b8860b', '#00838f', '#c2185b', '#5d6d7e'];
function clientColor(name) {
  let h = 0;
  for (const ch of String(name || '')) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return BLOCK_COLORS[h % BLOCK_COLORS.length];
}
function initials(w) {
  const a = (w.firstName || w.name || '').trim().charAt(0);
  const b = (w.lastName || '').trim().charAt(0);
  return (a + b).toUpperCase() || '?';
}

export default function WorkerCalendar({ assignments = [], workers = [], clients = [], shifts = [], onSchedule, onDeleteSchedule, onUpdateSchedule, workerOrder = [], onSetWorkerOrder, onNavigate, skin = '' }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [weekDropdownOpen, setWeekDropdownOpen] = useState(false);
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [scheduleClient, setScheduleClient] = useState('');
  const [scheduleTimeStart, setScheduleTimeStart] = useState('08:30');
  const [scheduleTimeEnd, setScheduleTimeEnd] = useState('17:00');
  const [scheduleExtraDays, setScheduleExtraDays] = useState(new Set());
  // { 'YYYY-WNN': [workerId, ...] } — workers manually added to this week's view
  const [weekAddedWorkers, setWeekAddedWorkers] = useState(() => {
    try {
      const d = localStorage.getItem('ozel_week_added_workers');
      return d ? JSON.parse(d) : {};
    } catch { return {}; }
  });

  useEffect(() => {
    try { localStorage.setItem('ozel_week_added_workers', JSON.stringify(weekAddedWorkers)); } catch {}
  }, [weekAddedWorkers]);

  // Worker display order comes from props (lifted to Dashboard)
  const [workerSort, setWorkerSort] = useState(() => localStorage.getItem('ozel_worker_sort') || 'manual');
  const [locationFilter, setLocationFilter] = useState(null); // null | 'verolme' | 'schiedam' | 'amsterdam'
  const [calendarSearch, setCalendarSearch] = useState('');

  useEffect(() => {
    localStorage.setItem('ozel_worker_sort', workerSort);
  }, [workerSort]);

  // Close export dropdown when clicking outside
  useEffect(() => {
    if (!exportDropdownOpen) return;
    const handler = (e) => {
      if (!e.target.closest('.wk-dropdown-wrap')) {
        setExportDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [exportDropdownOpen]);

  // Quick-assign row: which day cell was clicked
  const [quickCell, setQuickCell] = useState(null); // { dateStr }
  const [quickExtraDays, setQuickExtraDays] = useState(new Set());
  const [quickWorker, setQuickWorker] = useState('');
  const [quickClient, setQuickClient] = useState('');
  const [quickTimeStart, setQuickTimeStart] = useState('08:30');
  const [quickTimeEnd, setQuickTimeEnd] = useState('17:00');
  const [quickError, setQuickError] = useState('');
  const [cellError, setCellError] = useState('');
  const [scheduleShiftId, setScheduleShiftId] = useState(null);
  const [quickShiftId, setQuickShiftId] = useState(null);
  const [copyWeekModal, setCopyWeekModal] = useState(null); // null or { items: [...] }
  const [editingAssignment, setEditingAssignment] = useState(null); // assignment being edited, or null
  const [editingDayIds, setEditingDayIds] = useState(new Set()); // IDs of assignments to update together

  const weekDays = useMemo(() => getWeekDaysMondayFirst(currentDate), [currentDate]);
  const weekNumber = getWeekNumber(weekDays[0]);
  const weekYear = weekDays[3].getFullYear();
  const weekKey = `${weekYear}-W${weekNumber}`;

  const dateRangeStr = useMemo(() => {
    const s = weekDays[0], e = weekDays[6];
    if (s.getMonth() === e.getMonth())
      return `${s.getDate()} - ${e.getDate()} ${MONTH_NAMES_NL[s.getMonth()]} ${e.getFullYear()}`;
    return `${s.getDate()} ${MONTH_NAMES_NL[s.getMonth()]} - ${e.getDate()} ${MONTH_NAMES_NL[e.getMonth()]} ${e.getFullYear()}`;
  }, [weekDays]);

  const getScheduleForCell = (workerId, dateStr) =>
    assignments.filter((s) => s.workerId === workerId && s.date === dateStr);

  const parseHours = (timeStr) => {
    if (!timeStr) return 0;
    const [startStr, endStr] = timeStr.split(' - ');
    if (!startStr || !endStr) return 0;
    const [sh, sm] = startStr.split(':').map(Number);
    const [eh, em] = endStr.split(':').map(Number);
    let raw = (eh + em / 60) - (sh + sm / 60);
    if (raw <= 0) raw += 24; // overnight shift
    return raw > 0 ? raw - 0.5 : 0;
  };

  const getDayTotalHours = (dateStr) =>
    assignments.filter((s) => s.date === dateStr).reduce((sum, s) => sum + parseHours(s.time), 0);

  const getWorkerWeekHours = (workerId) =>
    weekDays.reduce((sum, day) =>
      sum + getScheduleForCell(workerId, toDateStr(day)).reduce((s2, sc) => s2 + parseHours(sc.time), 0), 0);

  const getWorkerWeekDays = (workerId) =>
    weekDays.filter((day) => getScheduleForCell(workerId, toDateStr(day)).length > 0).length;

  const totalWeekHours = useMemo(() =>
    weekDays.reduce((sum, day) => sum + getDayTotalHours(toDateStr(day)), 0),
    [weekDays, assignments]);

  const getWorkerCountForDay = (dateStr) =>
    new Set(assignments.filter((s) => s.date === dateStr).map((s) => s.workerId)).size;

  // Workers with at least one assignment this week
  const workerIdsWithSchedules = useMemo(() => {
    const weekDateStrs = new Set(weekDays.map(toDateStr));
    return new Set(assignments.filter((s) => weekDateStrs.has(s.date)).map((s) => s.workerId));
  }, [assignments, weekDays]);

  const manuallyAddedIds = useMemo(() => new Set(weekAddedWorkers[weekKey] || []), [weekAddedWorkers, weekKey]);
  const visibleWorkerIds = useMemo(() => new Set([...workerIdsWithSchedules, ...manuallyAddedIds]), [workerIdsWithSchedules, manuallyAddedIds]);

  const visibleWorkers = useMemo(() => workers.filter((w) => visibleWorkerIds.has(w.id)), [workers, visibleWorkerIds]);

  const filteredVisibleWorkers = useMemo(() => {
    if (!locationFilter) return visibleWorkers;
    const keyword = locationFilter; // komt overeen met een deel van de klantnaam
    const weekDateStrs = new Set(weekDays.map(toDateStr));
    const workerIdsAtLocation = new Set(
      assignments
        .filter((a) => weekDateStrs.has(a.date) && (a.client || '').toLowerCase().includes(keyword))
        .map((a) => a.workerId)
    );
    return visibleWorkers.filter((w) => workerIdsAtLocation.has(w.id));
  }, [visibleWorkers, locationFilter, assignments, weekDays]);

  const searchedVisibleWorkers = useMemo(() => {
    if (!calendarSearch.trim()) return filteredVisibleWorkers;
    const q = fold(calendarSearch).trim();
    return filteredVisibleWorkers.filter((w) => fold(w.name).includes(q));
  }, [filteredVisibleWorkers, calendarSearch]);

  const orderedVisibleWorkers = useMemo(() => {
    const sorted = [...searchedVisibleWorkers];
    if (workerSort === 'az') {
      sorted.sort((a, b) => a.name.localeCompare(b.name, 'nl'));
    } else if (workerSort === 'za') {
      sorted.sort((a, b) => b.name.localeCompare(a.name, 'nl'));
    } else if (workerSort === 'hours-desc') {
      sorted.sort((a, b) => getWorkerWeekHours(b.id) - getWorkerWeekHours(a.id));
    } else if (workerSort === 'hours-asc') {
      sorted.sort((a, b) => getWorkerWeekHours(a.id) - getWorkerWeekHours(b.id));
    } else if (workerSort === 'days-desc') {
      sorted.sort((a, b) => getWorkerWeekDays(b.id) - getWorkerWeekDays(a.id));
    } else {
      // manual
      sorted.sort((a, b) => {
        const ia = workerOrder.indexOf(a.id);
        const ib = workerOrder.indexOf(b.id);
        if (ia === -1 && ib === -1) return 0;
        if (ia === -1) return 1;
        if (ib === -1) return -1;
        return ia - ib;
      });
    }
    return sorted;
  }, [searchedVisibleWorkers, workerOrder, workerSort, weekDays, assignments]);

  const moveWorker = (workerId, dir) => {
    const ids = orderedVisibleWorkers.map((w) => w.id);
    const idx = ids.indexOf(workerId);
    const newIdx = idx + dir;
    if (idx === -1 || newIdx < 0 || newIdx >= ids.length) return;
    const newIds = [...ids];
    [newIds[idx], newIds[newIdx]] = [newIds[newIdx], newIds[idx]];
    onSetWorkerOrder(newIds);
  };

  const totalWorkerDays = useMemo(() =>
    orderedVisibleWorkers.reduce((sum, w) => sum + getWorkerWeekDays(w.id), 0),
    [orderedVisibleWorkers, weekDays, assignments]);

  const unplannedWorkers = useMemo(() => workers.filter((w) => !visibleWorkerIds.has(w.id)), [workers, visibleWorkerIds]);

  const getWorkersNotOnDay = (dateStr) =>
    workers.filter((w) => !assignments.some((s) => s.workerId === w.id && s.date === dateStr));

  const openQuickModal = (dateStr) => {
    setQuickCell({ dateStr });
    setQuickExtraDays(new Set());
    setQuickWorker('');
    setQuickClient('');
    setQuickTimeStart('08:30');
    setQuickTimeEnd('17:00');
    setQuickError('');
    setQuickShiftId(null);
  };

  // Returns absolute [startMs, endMs] for a shift, handling overnight (end < start = next day)
  const shiftToRange = (dateStr, timeStr) => {
    const [startStr, endStr] = timeStr.split(' - ');
    if (!startStr || !endStr) return null;
    const base = new Date(dateStr + 'T00:00:00').getTime();
    const [sh, sm] = startStr.split(':').map(Number);
    const [eh, em] = endStr.split(':').map(Number);
    const startMs = base + (sh * 60 + sm) * 60000;
    let endMs   = base + (eh * 60 + em) * 60000;
    if (endMs <= startMs) endMs += 24 * 60 * 60000; // overnight
    return [startMs, endMs];
  };

  const REST_MS = 6 * 60 * 60 * 1000; // 6 hours minimum rest

  // Returns { assignment, type } if conflict found, else null
  // type: 'overlap' | 'rest'
  const findConflict = (workerId, newDate, newTime, excludeIds = null) => {
    const newRange = shiftToRange(newDate, newTime);
    if (!newRange) return null;
    const [ns, ne] = newRange;
    const excludeSet = excludeIds instanceof Set ? excludeIds : (excludeIds !== null ? new Set([excludeIds]) : new Set());
    for (const a of assignments) {
      if (a.workerId !== workerId) continue;
      if (excludeSet.has(a.id)) continue;
      const range = shiftToRange(a.date, a.time);
      if (!range) continue;
      const [as, ae] = range;
      if (ns < ae && as < ne) return { assignment: a, type: 'overlap' };
      if (ns < ae + REST_MS && as < ne + REST_MS) return { assignment: a, type: 'rest' };
    }
    return null;
  };

  const handleQuickSchedule = async () => {
    if (!quickCell || !quickWorker || !quickClient) return;
    const worker = workers.find((w) => w.id === parseInt(quickWorker));
    const newTime = `${quickTimeStart} - ${quickTimeEnd}`;
    const allDates = [quickCell.dateStr, ...Array.from(quickExtraDays)].sort();
    const conflicts = allDates
      .map((dateStr) => findConflict(worker.id, dateStr, newTime))
      .filter(Boolean);
    if (conflicts.length > 0) {
      const lines = conflicts.map(({ assignment: c, type }) => {
        const d = new Date(c.date + 'T00:00:00');
        const dayLabel = d.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' });
        return `${type === 'overlap' ? '⛔ Dubbel:' : '⏱ Rusttijd:'} ${dayLabel} ${c.time} (${c.client})`;
      });
      setQuickError(`Conflict voor ${worker.name}:\n${lines.join('\n')}`);
      return;
    }
    if (!window.confirm(`Weet je zeker dat je ${worker.name} wilt inplannen${allDates.length > 1 ? ` op ${allDates.length} dagen` : ''}?`)) return;
    for (const dateStr of allDates) {
      if (!onSchedule) break;
      const r = await onSchedule({
        workerId: worker.id,
        workerName: worker.name,
        skill: worker.skill || '',
        date: dateStr,
        time: newTime,
        client: quickClient,
        status: 'confirmed',
        shiftId: quickShiftId,
      });
      if (r && r.ok === false) { setQuickError(r.error); return; }
    }
    setQuickCell(null);
  };

  const [lastAddedWorkerId, setLastAddedWorkerId] = useState(null);

  const handleAddWorker = (workerIdStr) => {
    const id = parseInt(workerIdStr);
    if (!id) return;
    setWeekAddedWorkers((prev) => ({
      ...prev,
      [weekKey]: [...(prev[weekKey] || []), id],
    }));
    setLastAddedWorkerId(id);
  };

  useEffect(() => {
    if (!lastAddedWorkerId) return;
    const timer = setTimeout(() => {
      // Desktop: tabelrij; mobiel: kaart (de tabel is daar verborgen).
      const row = [...document.querySelectorAll(`[data-worker-id="${lastAddedWorkerId}"]`)]
        .find((el) => el.offsetParent !== null);
      if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setLastAddedWorkerId(null);
    }, 80);
    return () => clearTimeout(timer);
  }, [lastAddedWorkerId]);

  const openCopyWeek = () => {
    const prevWeekDays = weekDays.map((d) => {
      const prev = new Date(d);
      prev.setDate(d.getDate() - 7);
      return prev;
    });
    const prevWeekNum = getWeekNumber(prevWeekDays[0]);
    const prevDateStrs = new Set(prevWeekDays.map(toDateStr));
    const prevAssignments = assignments.filter((a) => prevDateStrs.has(a.date));

    if (prevAssignments.length === 0) {
      alert('Geen inplanningen gevonden in week ' + prevWeekNum + '.');
      return;
    }

    const items = prevAssignments.map((a) => {
      const dayIdx = prevWeekDays.findIndex((d) => toDateStr(d) === a.date);
      const newDate = toDateStr(weekDays[dayIdx]);
      const worker = workers.find((w) => w.id === a.workerId);
      const conflict = findConflict(a.workerId, newDate, a.time);
      return {
        assignment: a,
        newDate,
        workerName: worker?.name || a.workerName || 'Onbekend',
        workerId: a.workerId,
        conflict,
        selected: !conflict,
      };
    });

    setCopyWeekModal({ items, prevWeekNum });
  };

  const executeCopyWeek = async () => {
    if (!copyWeekModal) return;
    const toCreate = copyWeekModal.items.filter((item) => item.selected && !item.conflict);
    const blocked = new Map(); // workerName -> reason, deduped
    for (const item of toCreate) {
      const r = await onSchedule({
        workerId: item.assignment.workerId,
        workerName: item.workerName,
        skill: item.assignment.skill || '',
        date: item.newDate,
        time: item.assignment.time,
        client: item.assignment.client,
        status: 'confirmed',
        shiftId: item.assignment.shiftId || null,
      });
      // Collect instead of alerting per item — copying a week can hit the same
      // blocked worker many times over.
      if (r && r.ok === false) blocked.set(item.workerName, r.error);
    }
    setCopyWeekModal(null);
    if (blocked.size) {
      alert(
        `Niet alles is gekopieerd:\n\n${[...blocked.values()].join('\n')}`
      );
    }
  };

  const goToPreviousWeek = () => { const d = new Date(currentDate); d.setDate(d.getDate() - 7); setCurrentDate(d); };
  const goToNextWeek = () => { const d = new Date(currentDate); d.setDate(d.getDate() + 7); setCurrentDate(d); };
  const goToWeek = (year, week) => { setCurrentDate(getMondayOfWeek(year, week)); setWeekDropdownOpen(false); };

  const weekOptions = useMemo(() => {
    const opts = [];
    for (let w = 1; w <= 52; w++) {
      const mon = getMondayOfWeek(weekYear, w);
      const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
      opts.push({ week: w, year: weekYear, label: `Week ${w} — ${mon.getDate()} ${MONTH_NAMES_NL[mon.getMonth()]} - ${sun.getDate()} ${MONTH_NAMES_NL[sun.getMonth()]}` });
    }
    return opts;
  }, [weekYear]);

  const openCellModal = (workerId, dateStr) => {
    setSelectedCell({ workerId, dateStr });
    setScheduleClient('');
    setScheduleTimeStart('08:30');
    setScheduleTimeEnd('17:00');
    setScheduleExtraDays(new Set());
    setCellError('');
    setScheduleShiftId(null);
    setEditingAssignment(null);
    setEditingDayIds(new Set());
  };

  const handleCellSchedule = async () => {
    if (!selectedCell || !scheduleClient) return;
    const worker = workers.find((w) => w.id === selectedCell.workerId);
    const newTime = `${scheduleTimeStart} - ${scheduleTimeEnd}`;
    const allDates = [selectedCell.dateStr, ...Array.from(scheduleExtraDays)];

    const conflicts = allDates
      .map((dateStr) => ({ dateStr, found: findConflict(selectedCell.workerId, dateStr, newTime) }))
      .filter(({ found }) => found);

    if (conflicts.length > 0) {
      const lines = conflicts.map(({ found: { assignment: c, type } }) => {
        const d = new Date(c.date + 'T00:00:00');
        const dayLabel = d.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' });
        const prefix = type === 'overlap' ? '⛔ Dubbel:' : '⏱ Rusttijd:';
        return `${prefix} ${dayLabel} ${c.time} bij ${c.client}`;
      });
      setCellError(`Conflict voor ${worker?.name}:\n${lines.join('\n')}`);
      return;
    }

    if (!window.confirm(`Weet je zeker dat je deze wijziging wilt maken voor ${worker?.name}?`)) return;
    let blockedReason = '';
    for (const dateStr of allDates) {
      if (!onSchedule) break;
      const r = await onSchedule({
        workerId: selectedCell.workerId,
        workerName: worker?.name || 'Worker',
        skill: worker?.skill || '',
        date: dateStr,
        time: newTime,
        client: scheduleClient,
        status: 'confirmed',
        shiftId: scheduleShiftId,
      });
      // Same worker for every date, so one failure means all of them fail.
      if (r && r.ok === false) { blockedReason = r.error; break; }
    }
    if (blockedReason) { setCellError(blockedReason); return; }
    setSelectedCell(null);
  };

  const handleEditSchedule = () => {
    if (!editingAssignment || !scheduleClient || editingDayIds.size === 0) return;
    const newTime = `${scheduleTimeStart} - ${scheduleTimeEnd}`;
    const toUpdate = assignments.filter((a) => editingDayIds.has(a.id));
    const conflicts = toUpdate
      .map((a) => ({ a, conflict: findConflict(a.workerId, a.date, newTime, editingDayIds) }))
      .filter(({ conflict }) => conflict);
    if (conflicts.length > 0) {
      const lines = conflicts.map(({ a, conflict: { assignment: c, type } }) => {
        const d = new Date(c.date + 'T00:00:00');
        const dayLabel = d.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' });
        const prefix = type === 'overlap' ? '⛔ Dubbel:' : '⏱ Rusttijd:';
        return `${prefix} ${dayLabel} ${c.time} (${c.client})`;
      });
      setCellError(lines.join('\n'));
      return;
    }
    toUpdate.forEach((a) => {
      if (onUpdateSchedule) onUpdateSchedule({ id: a.id, date: a.date, time: newTime, client: scheduleClient });
    });
    setEditingAssignment(null);
    setSelectedCell(null);
  };

  const handleDeleteSchedule = (id) => {
    const assignment = assignments.find(a => a.id === id);
    const name = assignment?.workerName || 'deze medewerker';
    if (!window.confirm(`Weet je zeker dat je deze wijziging wilt maken voor ${name}?`)) return;
    if (onDeleteSchedule) onDeleteSchedule(id);
  };

  const handleRemoveWorker = (worker) => {
    const confirmed = window.confirm(`Medewerker uit rooster verwijderen?`);
    if (!confirmed) return;

    // Delete all assignments for this worker in the current week
    const weekDateStrs = new Set(weekDays.map(toDateStr));
    const workerAssignments = assignments.filter(
      (a) => a.workerId === worker.id && weekDateStrs.has(a.date)
    );
    workerAssignments.forEach((a) => {
      if (onDeleteSchedule) onDeleteSchedule(a.id);
    });

    // Remove from weekAddedWorkers
    setWeekAddedWorkers((prev) => {
      const existing = prev[weekKey] || [];
      const updated = existing.filter((id) => id !== worker.id);
      return { ...prev, [weekKey]: updated };
    });
  };

  const isToday = (date) => {
    const t = new Date();
    return date.getDate() === t.getDate() && date.getMonth() === t.getMonth() && date.getFullYear() === t.getFullYear();
  };

  const generateScheduleImage = (worker) => {
    const scale = 2; // retina
    const W = 860, H = 310;
    const canvas = document.createElement('canvas');
    canvas.width = W * scale;
    canvas.height = H * scale;
    const ctx = canvas.getContext('2d');
    ctx.scale(scale, scale);

    // Background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, W, H);

    // Top bar
    ctx.fillStyle = '#e65100';
    ctx.fillRect(0, 0, W, 52);

    // Week label in top bar
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 16px Arial, sans-serif';
    ctx.fillText(`Week ${weekNumber}  •  ${dateRangeStr}`, 18, 34);

    // Worker name
    ctx.fillStyle = '#111111';
    ctx.font = 'bold 16px Arial, sans-serif';
    ctx.fillText(worker.name, 18, 76);
    ctx.fillStyle = '#888888';
    ctx.font = '12px Arial, sans-serif';
    ctx.fillText(worker.skill || '', 18, 92);

    // Week total (top right)
    const wHours = getWorkerWeekHours(worker.id);
    ctx.fillStyle = '#e65100';
    ctx.font = 'bold 20px Arial, sans-serif';
    const totalStr = `${wHours.toFixed(1).replace('.', ',')} uur`;
    const tw = ctx.measureText(totalStr).width;
    ctx.fillText(totalStr, W - tw - 18, 76);
    ctx.fillStyle = '#aaaaaa';
    ctx.font = '11px Arial, sans-serif';
    const subStr = 'totaal deze week';
    const sw = ctx.measureText(subStr).width;
    ctx.fillText(subStr, W - sw - 18, 91);

    // Grid
    const gridX = 18, gridY = 108;
    const colW = (W - 36) / 7;
    const cellH = 68;

    weekDays.forEach((day, i) => {
      const x = gridX + i * colW;
      const dateStr = toDateStr(day);
      const cells = getScheduleForCell(worker.id, dateStr);
      const has = cells.length > 0;
      const today = isToday(day);

      // Day label
      ctx.fillStyle = today ? '#e65100' : '#555555';
      ctx.font = `bold 11px Arial, sans-serif`;
      ctx.fillText(`${DAY_ABBR_NL[i].toUpperCase()} ${day.getDate()}`, x + 6, gridY + 14);

      // Cell bg
      const cellY = gridY + 20;
      ctx.fillStyle = has ? '#c8e6c9' : '#ffcdd2';
      ctx.fillRect(x + 2, cellY, colW - 4, cellH);

      // Today ring
      if (today) {
        ctx.strokeStyle = '#e65100';
        ctx.lineWidth = 1.5;
        ctx.strokeRect(x + 2, cellY, colW - 4, cellH);
      }

      if (has) {
        const s = cells[0];
        ctx.fillStyle = '#1b1b1b';
        ctx.font = 'bold 10px Arial, sans-serif';
        // Clip text to cell width
        const maxW = colW - 12;
        const timeStr = s.time || '';
        ctx.fillText(timeStr, x + 6, cellY + 18, maxW);
        ctx.fillStyle = '#444444';
        ctx.font = '10px Arial, sans-serif';
        const clientStr = s.client || '';
        ctx.fillText(clientStr, x + 6, cellY + 34, maxW);
        const hrs = parseHours(s.time);
        ctx.fillStyle = '#2e7d32';
        ctx.font = 'bold 10px Arial, sans-serif';
        ctx.fillText(`${hrs.toFixed(1).replace('.', ',')}u`, x + 6, cellY + 50, maxW);
      } else {
        ctx.fillStyle = 'rgba(0,0,0,0.2)';
        ctx.font = '18px Arial, sans-serif';
        ctx.fillText('—', x + colW / 2 - 5, cellY + cellH / 2 + 6);
      }
    });

    return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  };

  const exportToCSV = (workerFilter = null) => {
    // workerFilter = worker object → export only that worker; null → export all visible workers
    const targetWorkers = workerFilter ? [workerFilter] : visibleWorkers;

    // Collect all data rows (assignments only, no empty day rows)
    const dataRows = [];
    targetWorkers.forEach((worker) => {
      weekDays.forEach((day, i) => {
        const dateStr = toDateStr(day);
        const cells = getScheduleForCell(worker.id, dateStr);
        cells.forEach((s) => {
          const [startStr, endStr] = (s.time || '').split(' - ');
          const hrs = parseHours(s.time);
          // Format date as DD-MM-YYYY
          const dd = String(day.getDate()).padStart(2, '0');
          const mm = String(day.getMonth() + 1).padStart(2, '0');
          const yyyy = day.getFullYear();
          dataRows.push({
            _sortDate: dateStr,
            _sortName: worker.name,
            cols: [
              worker.name,
              worker.skill || '',
              `${dd}-${mm}-${yyyy}`,
              DAY_FULL_NL[i],
              startStr || '',
              endStr || '',
              hrs > 0 ? hrs.toFixed(1).replace('.', ',') : '0',
              s.client || '',
              s.status || '',
            ],
          });
        });
      });
    });

    // Sort by date ascending, then worker name
    dataRows.sort((a, b) => {
      if (a._sortDate < b._sortDate) return -1;
      if (a._sortDate > b._sortDate) return 1;
      return a._sortName.localeCompare(b._sortName, 'nl');
    });

    const escape = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const toLine = (cols) => cols.map(escape).join(';');

    const lines = [];
    // Header
    lines.push(toLine(['Medewerker', 'Vakgebied', 'Datum', 'Dag', 'Start', 'Eind', 'Uren', 'Klant', 'Status']));
    dataRows.forEach((r) => lines.push(toLine(r.cols)));

    // Summary section when exporting all workers
    if (!workerFilter) {
      lines.push('');
      lines.push(escape(`Totaaloverzicht week ${weekNumber}`));
      targetWorkers.forEach((worker) => {
        const wDays = getWorkerWeekDays(worker.id);
        const wHours = getWorkerWeekHours(worker.id);
        lines.push(toLine([worker.name, `${wDays} dagen`, wHours > 0 ? wHours.toFixed(1).replace('.', ',') : '0']));
      });
    }

    const csv = lines.join('\r\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const safeName = workerFilter
      ? workerFilter.name.replace(/\s+/g, '-')
      : 'iedereen';
    a.href = url;
    a.download = `rooster-week${weekNumber}-${weekYear}-${safeName}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportToPDF = async (workerFilter = null) => {
    if (workerFilter === null && visibleWorkers.length === 0) return;
    if (workerFilter !== null) {
      // Per-worker: canvas image export (existing behaviour)
      setPdfLoading(true);
      setExportDropdownOpen(false);
      try {
        const blob = await generateScheduleImage(workerFilter);
        const dataUrl = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
        const title = `Rooster Week ${weekNumber} \u2014 ${workerFilter.name}`;
        const html = `<!DOCTYPE html>
<html lang="nl"><head><meta charset="UTF-8"/><title>${title}</title>
<style>
  @page { size: A4 portrait; margin: 20mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; background: #fff; }
  .header { border-bottom: 2px solid #e65100; padding-bottom: 10px; margin-bottom: 16px; }
  .header h1 { font-size: 16px; font-weight: 800; color: #e65100; }
  img { width: 100%; display: block; }
  .footer { position: fixed; bottom: 12mm; left: 20mm; right: 20mm; font-size: 10px; color: #bbb; text-align: center; border-top: 1px solid #eee; padding-top: 6px; }
</style></head>
<body>
  <div class="header"><h1>${title}</h1></div>
  <img src="${dataUrl}" alt="${workerFilter.name}" />
  <div class="footer">ozel.eu &bull; Week ${weekNumber} &bull; ${dateRangeStr}</div>
  <script>window.onload=function(){setTimeout(function(){window.print();},250);};<\/script>
</body></html>`;
        const printWin = window.open('', '_blank');
        if (!printWin) { alert('Pop-up geblokkeerd. Sta pop-ups toe voor ozel.eu om PDF te exporteren.'); return; }
        printWin.document.write(html);
        printWin.document.close();
      } finally { setPdfLoading(false); }
      return;
    }

    // Iedereen: render HTML calendar table (mirrors the page view)
    setPdfLoading(true);
    setExportDropdownOpen(false);
    try {
      const title = `Rooster Week ${weekNumber} \u2014 ${dateRangeStr}`;

      const dayHeaders = weekDays.map((day, i) => {
        const today = isToday(day);
        return `<th style="${today ? 'color:#e65100;' : ''}">${DAY_ABBR_NL[i].toUpperCase()}<br/><span style="font-size:15px;">${day.getDate()}</span></th>`;
      }).join('');

      const workerRows = orderedVisibleWorkers.map((worker) => {
        const wHours = getWorkerWeekHours(worker.id);
        const wDays = getWorkerWeekDays(worker.id);
        const dayCells = weekDays.map((day) => {
          const dateStr = toDateStr(day);
          const cells = getScheduleForCell(worker.id, dateStr);
          const today = isToday(day);
          const bg = cells.length > 0 ? '#e8f5e9' : '#ffebee';
          const border = today ? 'outline: 2px solid #e65100;' : '';
          const content = cells.length > 0
            ? cells.map(s => `<div style="font-weight:700;font-size:10px;">${s.time || ''}</div><div style="font-size:9px;color:#555;">${s.client || ''}</div>`).join('')
            : '<span style="color:rgba(0,0,0,0.2);font-size:13px;">—</span>';
          return `<td style="background:${bg};${border}padding:5px 4px;text-align:center;vertical-align:middle;">${content}</td>`;
        }).join('');
        return `<tr>
          <td style="padding:6px 10px;white-space:nowrap;border-right:2px solid #eee;">
            <div style="font-weight:700;font-size:12px;">${worker.name}</div>
          </td>
          ${dayCells}
          <td style="text-align:center;padding:6px 8px;background:#fafafa;border-left:2px solid #eee;">
            <div style="font-size:10px;color:#888;">${wDays}d</div>
            <div style="font-size:12px;font-weight:700;color:#333;">${wHours > 0 ? wHours.toFixed(1).replace('.', ',') : '0'}u</div>
          </td>
        </tr>`;
      }).join('');

      const footerCells = weekDays.map((day) => {
        const dateStr = toDateStr(day);
        const count = getWorkerCountForDay(dateStr);
        const hrs = getDayTotalHours(dateStr);
        return `<td style="text-align:center;padding:5px 4px;background:#f5f5f5;">
          <div style="font-size:9px;color:#888;">${count} mdw</div>
          <div style="font-size:10px;font-weight:700;color:#e65100;">${hrs > 0 ? hrs.toFixed(1).replace('.', ',') : '—'}u</div>
        </td>`;
      }).join('');

      const html = `<!DOCTYPE html>
<html lang="nl"><head><meta charset="UTF-8"/><title>${title}</title>
<style>
  @page { size: A4 landscape; margin: 12mm 14mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; font-size: 11px; color: #111; background: #fff; }
  .header { border-bottom: 2px solid #e65100; padding-bottom: 8px; margin-bottom: 12px; display: flex; justify-content: space-between; align-items: flex-end; }
  .header h1 { font-size: 15px; font-weight: 800; color: #e65100; }
  .header .total { font-size: 11px; color: #888; }
  table { width: 100%; border-collapse: collapse; }
  th { background: #f5f5f5; padding: 6px 4px; text-align: center; font-size: 10px; border-bottom: 2px solid #ddd; }
  th:first-child { text-align: left; padding-left: 10px; }
  td { border-bottom: 1px solid #f0f0f0; }
  .footer { position: fixed; bottom: 6mm; left: 14mm; right: 14mm; font-size: 9px; color: #bbb; text-align: center; border-top: 1px solid #eee; padding-top: 4px; }
</style></head>
<body>
  <div class="header">
    <h1>${title}</h1>
    <span class="total">${totalWeekHours.toFixed(1).replace('.', ',')} uur totaal &bull; ${visibleWorkers.length} medewerkers</span>
  </div>
  <table>
    <thead>
      <tr>
        <th style="width:130px;">Medewerker</th>
        ${dayHeaders}
        <th>Uren</th>
      </tr>
    </thead>
    <tbody>${workerRows}</tbody>
    <tfoot>
      <tr>
        <td style="padding:5px 10px;font-weight:700;font-size:10px;background:#f5f5f5;">Totaal</td>
        ${footerCells}
        <td style="text-align:center;padding:5px 8px;background:#f5f5f5;font-weight:700;font-size:11px;color:#e65100;">${totalWeekHours.toFixed(1).replace('.', ',')}u</td>
      </tr>
    </tfoot>
  </table>
  <div class="footer">ozel.eu &bull; Week ${weekNumber} &bull; ${dateRangeStr}</div>
  <script>window.onload=function(){setTimeout(function(){window.print();},250);};<\/script>
</body></html>`;

      const printWin = window.open('', '_blank');
      if (!printWin) { alert('Pop-up geblokkeerd. Sta pop-ups toe voor ozel.eu om PDF te exporteren.'); return; }
      printWin.document.write(html);
      printWin.document.close();
    } finally { setPdfLoading(false); }
  };

  // WhatsApp: als er een nummer gekoppeld is (tab WhatsApp) verstuurt de server
  // het rooster direct. Anders, of als dat mislukt, de oude wa.me-link.
  // 'Snel inplannen' blijft onder de kop staan tijdens scrollen: kophoogte meten.
  const theadRef = useRef(null);
  const [headH, setHeadH] = useState(40);
  useEffect(() => {
    const el = theadRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setHeadH(el.getBoundingClientRect().height));
    ro.observe(el);
    return () => ro.disconnect();
  });

  const [waLinked, setWaLinked] = useState(false);
  const [waBusyId, setWaBusyId] = useState(null);
  // Per medewerker: laatste WhatsApp-poging in de getoonde week (uit rooster_whatsapp_log).
  const [waWeek, setWaWeek] = useState({});
  useEffect(() => {
    authFetch('/api/whatsapp/status').then((r) => r.json())
      .then((st) => setWaLinked(st.state === 'linked')).catch(() => setWaLinked(false));
  }, []);
  // Notities per medewerker per dag (bijv. "ziek", "vrij")
  const [notes, setNotes] = useState({}); // key `${workerId}|${date}` -> tekst
  const [noteDraft, setNoteDraft] = useState('');
  const [noteBusy, setNoteBusy] = useState(false);
  const noteKey = (workerId, dateStr) => `${workerId}|${dateStr}`;
  useEffect(() => {
    if (!weekDays.length) return;
    const from = toDateStr(weekDays[0]);
    const to = toDateStr(weekDays[weekDays.length - 1]);
    authFetch(`/api/planning-notes?from=${from}&to=${to}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => {
        const m = {};
        for (const n of rows || []) m[noteKey(n.workerId, n.date)] = n.note;
        setNotes(m);
      })
      .catch(() => {});
  }, [weekDays]);
  useEffect(() => {
    setNoteDraft(selectedCell ? (notes[noteKey(selectedCell.workerId, selectedCell.dateStr)] || '') : '');
  }, [selectedCell]); // eslint-disable-line react-hooks/exhaustive-deps
  const saveNote = async (text) => {
    if (!selectedCell) return;
    setNoteBusy(true);
    try {
      const r = await authFetch('/api/planning-notes', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workerId: selectedCell.workerId, date: selectedCell.dateStr, note: text }),
      });
      if (!r.ok) throw new Error();
      const key = noteKey(selectedCell.workerId, selectedCell.dateStr);
      setNotes((m) => {
        const n = { ...m };
        if (text.trim()) n[key] = text.trim(); else delete n[key];
        return n;
      });
      setNoteDraft(text.trim());
    } catch {
      window.alert('Notitie opslaan mislukt. Probeer opnieuw.');
    } finally {
      setNoteBusy(false);
    }
  };

  const loadWaWeek = () => authFetch(`/api/whatsapp/week-status?jaar=${weekYear}&week=${weekNumber}`)
    .then((r) => (r.ok ? r.json() : {})).then((d) => setWaWeek(d || {})).catch(() => {});
  useEffect(() => { setWaWeek({}); loadWaWeek(); }, [weekYear, weekNumber]);

  // Verzending naar meerdere mensen: draait op de server, wij volgen de voortgang.
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkJob, setBulkJob] = useState(null);
  const bulkRunning = !!bulkJob && !bulkJob.done;
  useEffect(() => {
    authFetch('/api/whatsapp/bulk').then((r) => (r.ok ? r.json() : {})).then((d) => setBulkJob(d.job || null)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!bulkRunning) return undefined;
    const t = setInterval(() => {
      authFetch('/api/whatsapp/bulk').then((r) => (r.ok ? r.json() : {})).then((d) => {
        if (d.job) setBulkJob(d.job);
        loadWaWeek();
      }).catch(() => {});
    }, 2000);
    return () => clearInterval(t);
  }, [bulkRunning, weekYear, weekNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  const fmtWaTime = (t) => {
    const d = new Date(t);
    return `${d.toLocaleDateString('nl-NL', { weekday: 'short' })} ${d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}`;
  };
  // Knopkleur + tekst onder de naam + tooltip, afhankelijk van de laatste poging.
  const waInfo = (workerId) => {
    const w = waWeek[workerId];
    if (!w) return { cls: '', label: '', title: '' };
    if (w.status === 'verstuurd') {
      const keer = w.verstuurd > 1 ? ` (${w.verstuurd}×)` : '';
      return { cls: 'sent', label: `WA ✓ ${fmtWaTime(w.at)}`, title: `Verstuurd via WhatsApp op ${fmtWaTime(w.at)}${keer}${w.door ? ` door ${w.door}` : ''} — klik om opnieuw te sturen` };
    }
    if (w.status === 'geopend') {
      const eerder = w.laatstVerstuurd ? ` Eerder wel verstuurd op ${fmtWaTime(w.laatstVerstuurd)}.` : '';
      return { cls: 'link', label: `WA link ${fmtWaTime(w.at)}`, title: `WhatsApp-link geopend op ${fmtWaTime(w.at)}, niet bevestigd dat het verstuurd is.${eerder}` };
    }
    return { cls: 'failed', label: 'WA mislukt', title: `Versturen mislukt op ${fmtWaTime(w.at)}: ${w.fout || 'onbekende fout'}${w.laatstVerstuurd ? `. Eerder wel verstuurd op ${fmtWaTime(w.laatstVerstuurd)}.` : ''}` };
  };

  // Zelfde begroeting als bij versturen naar meerdere mensen.
  const roosterText = (worker) => {
    const voornaam = String(worker.firstName || worker.name || '').trim().split(/\s+/)[0] || '';
    return `Hallo${voornaam ? ` ${voornaam}` : ''},\n\nHier is je rooster voor week ${weekNumber} (${dateRangeStr}).\n\nWil je het rooster bevestigen? Reageer met een ✅, 👍 of "akkoord".`;
  };

  const handleSendWhatsApp = async (worker) => {
    if (bulkRunning) { alert('Er loopt nu een verzending naar meerdere mensen. Wacht tot die klaar is.'); setBulkOpen(true); return; }
    if (waLinked) {
      if (!worker.phone) { alert(`Geen telefoonnummer bekend voor ${worker.name}.`); return; }
      setWaBusyId(worker.id);
      try {
        const blob = await generateScheduleImage(worker);
        const fd = new FormData();
        fd.append('phone', worker.phone);
        fd.append('name', worker.name);
        fd.append('worker_id', worker.id);
        fd.append('jaar', weekYear);
        fd.append('week', weekNumber);
        fd.append('text', roosterText(worker));
        fd.append('image', new File([blob], `rooster-week${weekNumber}.png`, { type: 'image/png' }));
        const res = await authFetch('/api/whatsapp/send', { method: 'POST', body: fd });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Versturen mislukt');
        return;
      } catch (err) {
        loadWaWeek();
        if (!confirm(`WhatsApp versturen naar ${worker.name} mislukt: ${err.message}\n\nWhatsApp-link openen om het handmatig te sturen?`)) return;
      } finally {
        setWaBusyId(null);
        loadWaWeek();
      }
    }
    const blob = await generateScheduleImage(worker);
    const fileName = `rooster-week${weekNumber}-${worker.name.replace(/\s+/g, '-')}.png`;
    const phone = (worker.phone || '').replace(/\D/g, '');

    // Upload image to server to get a public URL
    const fd = new FormData();
    fd.append('image', new File([blob], fileName, { type: 'image/png' }));
    const res = await fetch('/api/upload-schedule', { method: 'POST', body: fd });
    const { url: imageUrl } = await res.json();

    const text = `${roosterText(worker)}\n${imageUrl}`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, '_blank');
    authFetch('/api/whatsapp/log-link', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ worker_id: worker.id, jaar: weekYear, week: weekNumber, phone }),
    }).then(loadWaWeek).catch(() => {});
  };

  const handleShareImage = async (worker) => {
    const blob = await generateScheduleImage(worker);
    const fileName = `rooster-week${weekNumber}-${worker.name.replace(/\s+/g, '-')}.png`;
    const file = new File([blob], fileName, { type: 'image/png' });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: `Rooster ${worker.name} — week ${weekNumber}`,
          text: roosterText(worker),
        });
      } catch (err) {
        if (err.name !== 'AbortError') alert('Delen mislukt.');
      }
    } else {
      // Fallback: download the image
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  // ── Mail rooster ──────────────────────────────────────────────────────────
  const [mailBusyId, setMailBusyId] = useState(null);
  const [bulkMail, setBulkMail] = useState(null); // null | { og, sending, note, resend }
  const [mailedIds, setMailedIds] = useState(() => new Set()); // workers already mailed THIS week

  // Load who's already been mailed for the currently-shown week (resets on week change).
  useEffect(() => {
    let cancelled = false;
    setMailedIds(new Set());
    authFetch(`/api/planning/mail-status?jaar=${weekYear}&week=${weekNumber}`)
      .then((r) => (r.ok ? r.json() : { workerIds: [] }))
      .then((d) => { if (!cancelled) setMailedIds(new Set((d.workerIds || []).map(Number))); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [weekYear, weekNumber]);

  // Send one worker their week rooster (PNG) to their own email, from the werkgever domain.
  const mailWorkerRooster = async (worker) => {
    const email = (worker.email || '').trim();
    if (!email) throw new Error(`${worker.name} heeft geen e-mailadres`);
    const blob = await generateScheduleImage(worker);
    const imageBase64 = await blobToBase64(blob);
    const profiel = opdrachtgeverToProfiel(worker.opdrachtgeverName);
    const filename = `rooster-week${weekNumber}-${worker.name.replace(/\s+/g, '-')}.png`;
    const text = `Beste ${worker.name},\n\nIn de bijlage je rooster voor week ${weekNumber} (${dateRangeStr}).\n\nMet vriendelijke groet`;
    const res = await authFetch('/api/planning/mail', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        profiel, to: email, subject: `Rooster week ${weekNumber} — ${worker.name}`,
        text, filename, imageBase64, workerName: worker.name,
        workerId: worker.id, jaar: weekYear, week: weekNumber,
      }),
    });
    if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'versturen mislukt'); }
    setMailedIds((prev) => new Set(prev).add(worker.id));
  };

  const handleMailWorker = async (worker) => {
    if (mailBusyId) return;
    if (!(worker.email || '').trim()) { alert(`${worker.name} heeft geen e-mailadres in de database.`); return; }
    if (mailedIds.has(worker.id) && !window.confirm(`${worker.name} heeft het rooster van week ${weekNumber} al gehad. Nog een keer sturen?`)) return;
    setMailBusyId(worker.id);
    try {
      await mailWorkerRooster(worker);
      alert(`Rooster van week ${weekNumber} gemaild naar ${worker.name} (${worker.email}).`);
    } catch (e) {
      alert('Mailen mislukt: ' + (e?.message || e));
    } finally {
      setMailBusyId(null);
    }
  };

  // Distinct werkgevers among the workers shown this week (for the bulk mail picker).
  // Eén klik = iedereen van deze week z'n eigen rooster, elk vanaf het domein
  // van zijn eigen werkgever. Geen menu, geen bedrijf kiezen.
  const startMailAll = async () => {
    if (bulkMail?.sending) return;
    const withEmail = visibleWorkers.filter((w) => (w.email || '').trim());
    const without = visibleWorkers.filter((w) => !(w.email || '').trim());

    if (withEmail.length === 0) {
      window.alert('Geen medewerkers met een e-mailadres in deze week.');
      return;
    }

    const fresh = withEmail.filter((w) => !mailedIds.has(w.id));
    const already = withEmail.length - fresh.length;
    let targets = fresh;
    let resend = false;

    if (fresh.length === 0) {
      if (!window.confirm(
        `Alle ${withEmail.length} medewerker(s) hebben hun rooster van week ${weekNumber} al gehad.\n\nToch opnieuw versturen?`
      )) return;
      targets = withEmail;
      resend = true;
    } else {
      const lines = [`Rooster van week ${weekNumber} mailen naar ${fresh.length} medewerker(s).`];
      if (already) lines.push(`${already} heeft het al gehad en wordt overgeslagen.`);
      if (without.length) lines.push(`${without.length} zonder e-mailadres wordt overgeslagen.`);
      lines.push('', 'Versturen?');
      if (!window.confirm(lines.join('\n'))) return;
    }

    setBulkMail({ sending: true, done: 0, total: targets.length, note: '', failedList: [] });
    let sent = 0;
    const failedList = [];
    for (const w of targets) {
      try { await mailWorkerRooster(w); sent++; }
      catch (e) { failedList.push(`${w.name}${e?.message ? ` — ${e.message}` : ''}`); }
      setBulkMail((m) => (m ? { ...m, done: sent + failedList.length } : m));
      await new Promise((r) => setTimeout(r, 120)); // rustig aan tegen limieten
    }
    const parts = [`${sent} verstuurd`];
    if (failedList.length) parts.push(`${failedList.length} mislukt`);
    if (already && !resend) parts.push(`${already} al gemaild overgeslagen`);
    if (without.length) parts.push(`${without.length} zonder e-mail overgeslagen`);
    setBulkMail((m) => ({ ...(m || {}), sending: false, note: parts.join(' · '), failedList }));
  };

  return (
    <div className="wk-calendar">
      {/* Header */}
      <div className="wk-top-bar">
        <div className="wk-top-left">
          <h2>Werktijden</h2>
          <span className="wk-badge">week {weekNumber}</span>
          <span className="wk-range">{dateRangeStr}</span>
        </div>
        <div className="wk-top-right">
          <div className="wk-nav">
            <button onClick={goToPreviousWeek}><ChevronLeft size={16} /></button>
            <button className="wk-nav-today" onClick={() => setCurrentDate(new Date())}>Deze week</button>
            <button onClick={goToNextWeek}><ChevronRight size={16} /></button>
          </div>
          <button className="wk-copy-week-btn" onClick={openCopyWeek} title="Kopieer inplanningen van vorige week naar deze week">
            Kopieer vorige week
          </button>
          <button
            className="wk-mail-week-btn"
            onClick={startMailAll}
            title="Mail het rooster naar alle medewerkers van een werkgever"
          >
            <Mail size={14} /> Mail roosters
          </button>
          <div className="wk-dropdown-wrap">
            <button className="wk-export-btn" onClick={() => setExportDropdownOpen(!exportDropdownOpen)} disabled={pdfLoading}>
              <Download size={14} /> {pdfLoading ? 'Bezig…' : 'Exporteren'} <ChevronDown size={13} />
            </button>
            {exportDropdownOpen && (
              <div className="wk-dropdown-list wk-export-list">
                <div className="wk-export-header">Exporteer week {weekNumber} als CSV</div>
                <div className="wk-dropdown-item" onClick={() => { exportToCSV(null); setExportDropdownOpen(false); }}>
                  <Download size={13} /> Iedereen deze week
                </div>
                {visibleWorkers.length > 0 && <div className="wk-export-divider">Per medewerker</div>}
                {visibleWorkers.map((w) => (
                  <div key={w.id} className="wk-dropdown-item" onClick={() => { exportToCSV(w); setExportDropdownOpen(false); }}>
                    {w.name}
                  </div>
                ))}
                <div className="wk-export-divider wk-export-divider-pdf">PDF exporteren</div>
                <div className="wk-dropdown-item wk-dropdown-item-pdf" onClick={() => exportToPDF(null)}>
                  <FileText size={13} /> Iedereen (PDF)
                </div>
                {visibleWorkers.map((w) => (
                  <div key={`pdf-${w.id}`} className="wk-dropdown-item wk-dropdown-item-pdf" onClick={() => exportToPDF(w)}>
                    <FileText size={13} /> {w.name} (PDF)
                  </div>
                ))}
              </div>
            )}
          </div>
          {visibleWorkers.length > 0 && (() => {
            const done = visibleWorkers.filter((w) => waWeek[w.id]?.laatstVerstuurd).length;
            const open = visibleWorkers.filter((w) => !waWeek[w.id]?.laatstVerstuurd).map((w) => w.name);
            return (
              <button type="button" className={`wk-wa-count ${done === visibleWorkers.length ? 'all' : ''} ${bulkRunning ? 'running' : ''}`}
                onClick={() => (waLinked || bulkJob ? setBulkOpen(true) : alert('Koppel eerst WhatsApp in het menu WhatsApp.'))}
                title={bulkRunning ? 'Bezig met versturen, klik voor de voortgang' : (open.length ? `Klik om te versturen. Nog niet verstuurd:\n${open.join('\n')}` : 'Iedereen heeft zijn rooster via WhatsApp gekregen')}>
                {bulkRunning ? (() => {
                  const fin = bulkJob.items.filter((x) => !['wachten', 'bezig'].includes(x.status)).length;
                  return <><Loader2 size={12} className="wk-spin" /> Versturen {fin}/{bulkJob.items.length}<span className="wk-wa-minibar"><span style={{ width: `${(fin / bulkJob.items.length) * 100}%` }} /></span></>;
                })() : <>WhatsApp {done}/{visibleWorkers.length}</>}
              </button>
            );
          })()}
          <div className="wk-dropdown-wrap">
            <button className="wk-dropdown-btn" onClick={() => setWeekDropdownOpen(!weekDropdownOpen)}>
              Week {weekNumber} <ChevronDown size={14} />
            </button>
            {weekDropdownOpen && (
              <div className="wk-dropdown-list">
                {weekOptions.map((o) => (
                  <div key={o.week} className={`wk-dropdown-item ${o.week === weekNumber ? 'active' : ''}`} onClick={() => goToWeek(o.year, o.week)}>
                    {o.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <WhatsAppBulk
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        workers={visibleWorkers}
        waWeek={waWeek}
        job={bulkJob}
        setJob={setBulkJob}
        weekYear={weekYear}
        weekNumber={weekNumber}
        dateRangeStr={dateRangeStr}
        makeImage={generateScheduleImage}
      />
      {bulkMail && (
        <div className="wk-mmodal-backdrop" onMouseDown={() => !bulkMail.sending && setBulkMail(null)}>
          <div className="wk-mmodal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="wk-mmodal-head">
              <span>Roosters mailen — week {weekNumber}</span>
              <button className="wk-mmodal-x" onClick={() => setBulkMail(null)} disabled={bulkMail.sending}><X size={16} /></button>
            </div>

            {bulkMail.sending ? (
              <p className="wk-mmodal-body">
                <Loader2 size={14} className="wk-spin" /> Versturen… <strong>{bulkMail.done || 0}</strong> van {bulkMail.total}
              </p>
            ) : (
              <p className="wk-mmodal-body">
                Iedere medewerker heeft zijn eigen rooster ontvangen, verstuurd vanaf het adres van zijn eigen werkgever.
              </p>
            )}

            {bulkMail.note && <div className="wk-mmodal-note">{bulkMail.note}</div>}
            {bulkMail.failedList?.length > 0 && (
              <ul className="wk-mmodal-failed">
                {bulkMail.failedList.map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            )}

            {!bulkMail.sending && (
              <div className="wk-mmodal-actions">
                <button className="wk-mmodal-cancel" onClick={() => setBulkMail(null)}>Sluiten</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filter + sort bar */}
      <div className="wk-filter-bar">
        <div className="wk-location-btns">
          <button className={`wk-loc-btn ${!locationFilter ? 'active' : ''}`} onClick={() => setLocationFilter(null)}>Alle</button>
          <button className={`wk-loc-btn ${locationFilter === 'verolme' ? 'active' : ''}`} onClick={() => setLocationFilter(locationFilter === 'verolme' ? null : 'verolme')}>Verolme</button>
          <button className={`wk-loc-btn ${locationFilter === 'schiedam' ? 'active' : ''}`} onClick={() => setLocationFilter(locationFilter === 'schiedam' ? null : 'schiedam')}>Schiedam</button>
          <button className={`wk-loc-btn ${locationFilter === 'amsterdam' ? 'active' : ''}`} onClick={() => setLocationFilter(locationFilter === 'amsterdam' ? null : 'amsterdam')}>Amsterdam</button>
        </div>
        <input
          className="wk-cal-search"
          type="text"
          placeholder="Zoek medewerker..."
          value={calendarSearch}
          onChange={(e) => setCalendarSearch(e.target.value)}
        />
        <select className="wk-sort-select" value={workerSort} onChange={(e) => setWorkerSort(e.target.value)}>
          <option value="manual">Volgorde: Handmatig</option>
          <option value="az">Volgorde: A → Z</option>
          <option value="za">Volgorde: Z → A</option>
          <option value="hours-desc">Volgorde: Meeste uren</option>
          <option value="hours-asc">Volgorde: Minste uren</option>
          <option value="days-desc">Volgorde: Meeste dagen</option>
        </select>
      </div>

      {/* Hours summary */}
      <div className="wk-summary">
        <span>{totalWorkerDays} dag{totalWorkerDays !== 1 ? 'en' : ''} ingepland</span>
        <span className="wk-summary-sep">·</span>
        <span>{totalWeekHours.toFixed(1).replace('.', ',')} uur deze week</span>
      </div>

      {/* ── MOBILE card view (hidden on desktop) ── */}
      <div className="wk-mobile-view">
        {/* Add worker */}
        {unplannedWorkers.length > 0 && (
          <div className="wk-mobile-add-select">
            <SearchableSelect
              options={unplannedWorkers.map((w) => ({ value: String(w.id), label: w.name, badge: w.vog ? 'VOG' : null }))}
              value=""
              onChange={(v) => handleAddWorker(v)}
              placeholder="＋ Medewerker toevoegen..."
              searchPlaceholder="Zoek medewerker..."
            />
          </div>
        )}

        {visibleWorkers.length === 0 && (
          <div className="wk-mobile-empty">Geen medewerkers ingepland deze week.</div>
        )}

        {orderedVisibleWorkers.map((worker) => {
          const wHours = getWorkerWeekHours(worker.id);
          const wDays = getWorkerWeekDays(worker.id);
          return (
            <div key={worker.id} className="wk-card" data-worker-id={worker.id}>
              {/* Card header */}
              <div className="wk-card-head">
                {skin === 'nieuw' && <span className="wk-avatar" aria-hidden="true">{initials(worker)}</span>}
                <div className="wk-card-info">
                  <span className="wk-card-name">{worker.name}</span>
                  {waInfo(worker.id).label && <span className={`wk-wa-status ${waInfo(worker.id).cls}`}>{waInfo(worker.id).label}</span>}
                  {worker.vog && <span className="wk-vog" title="VOG aanwezig">VOG</span>}
                  {worker.skill && <span className="wk-card-skill">{worker.skill}</span>}
                  {worker.opdrachtgeverName && <span className="wk-card-company">{worker.opdrachtgeverName}</span>}
                </div>
                <div className="wk-card-meta">
                  <span className="wk-card-hours">{wDays}d · {wHours > 0 ? wHours.toFixed(1).replace('.', ',') : '0'}u</span>
                  <button className={`wk-wa-btn ${waInfo(worker.id).cls}`} disabled={waBusyId === worker.id}
                    title={waInfo(worker.id).title || `Stuur rooster naar ${worker.name} via WhatsApp`}
                    onClick={(e) => { e.stopPropagation(); handleSendWhatsApp(worker); }}>
                    {waBusyId === worker.id ? <Loader2 size={13} className="wk-spin" /> : <svg viewBox="0 0 24 24" fill="currentColor" width="13" height="13"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.117 1.528 5.845L0 24l6.335-1.502A11.954 11.954 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.017-1.374l-.36-.214-3.732.885.936-3.619-.234-.373A9.818 9.818 0 1112 21.818z"/></svg>}
                  </button>
                  <button className="wk-share-btn" title="Deel als afbeelding" onClick={(e) => { e.stopPropagation(); handleShareImage(worker); }}>
                    <Share2 size={11} />
                  </button>
                  <button className={`wk-mail-btn ${mailedIds.has(worker.id) ? 'mailed' : ''}`}
                    title={mailedIds.has(worker.id) ? 'Rooster al gemaild deze week — klik om opnieuw te sturen' : `Mail rooster naar ${worker.name}`}
                    disabled={mailBusyId === worker.id}
                    onClick={(e) => { e.stopPropagation(); handleMailWorker(worker); }}>
                    {mailBusyId === worker.id ? <Loader2 size={11} className="wk-spin" /> : <Mail size={11} />}
                  </button>
                  <button className="wk-del-btn" onClick={(e) => { e.stopPropagation(); handleRemoveWorker(worker); }}>
                    <X size={11} />
                  </button>
                </div>
              </div>

              {/* Day pills */}
              <div className="wk-card-days">
                {weekDays.map((day, i) => {
                  const dateStr = toDateStr(day);
                  const cells = getScheduleForCell(worker.id, dateStr);
                  const has = cells.length > 0;
                  const today = isToday(day);
                  return (
                    <div
                      key={i}
                      className={`wk-day-pill ${has ? 'wk-pill-on' : 'wk-pill-off'} ${today ? 'wk-pill-today' : ''} ${notes[noteKey(worker.id, toDateStr(day))] ? 'wk-pill-note' : ''}`}
                      onClick={() => openCellModal(worker.id, dateStr)}
                    >
                      <span className="wk-pill-abbr">{DAY_ABBR_NL[i]}</span>
                      <span className="wk-pill-date">{day.getDate()}</span>
                      {has ? (
                        <>
                          <span className="wk-pill-time">{cells[0].time.split(' - ')[0]}</span>
                          <span className="wk-pill-time wk-pill-end">{cells[0].time.split(' - ')[1]}</span>
                        </>
                      ) : (
                        <span className="wk-pill-dash">—</span>
                      )}
                    </div>
                  );
                })}
              </div>

            </div>
          );
        })}

        {/* Quick-assign strip */}
        <div className="wk-mobile-quick">
          <span className="wk-mobile-quick-label">Snel inplannen</span>
          <div className="wk-mobile-quick-days">
            {weekDays.map((day, i) => (
              <button
                key={i}
                className={`wk-mobile-quick-btn ${isToday(day) ? 'wk-pill-today' : ''}`}
                onClick={() => openQuickModal(toDateStr(day))}
              >
                <span>{DAY_ABBR_NL[i]}</span>
                <span>{day.getDate()}</span>
                <Plus size={10} />
              </button>
            ))}
          </div>
        </div>

        {/* Day totals strip */}
        <div className="wk-mobile-totals">
          {weekDays.map((day, i) => {
            const dateStr = toDateStr(day);
            const count = getWorkerCountForDay(dateStr);
            const hours = getDayTotalHours(dateStr);
            return (
              <div key={i} className={`wk-mobile-total-cell ${isToday(day) ? 'wk-pill-today' : ''}`}>
                <span className="wk-mobile-total-abbr">{DAY_ABBR_NL[i]}</span>
                <span className="wk-mobile-total-count">{count} <Users size={9} /></span>
                <span className="wk-mobile-total-hrs">{hours > 0 ? hours.toFixed(1).replace('.', ',') : '—'}u</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── DESKTOP table view (hidden on mobile) ── */}
      <div className={`wk-desktop-view ${skin === 'nieuw' ? 'wk-nv-layout' : ''}`}>
      <div className="wk-nv-main">
      {/* Grid table */}
      <div className="wk-table-wrap">
        <table className="wk-table">
          <thead ref={theadRef}>
            <tr>
              <th className="wk-th-name">Medewerker</th>
              {weekDays.map((day, i) => (
                <th key={i} className={`wk-th-day ${isToday(day) ? 'wk-today' : ''}`}>
                  <span className="wk-th-abbr">{DAY_ABBR_NL[i]}</span>
                  <span className="wk-th-num">{day.getDate()}</span>
                </th>
              ))}
              <th className="wk-th-total">Dagen&nbsp;/ Uren</th>
            </tr>
          </thead>
          <tbody>
            {/* Add worker row — top */}
            {unplannedWorkers.length > 0 && (
              <tr className="wk-add-row">
                <td className="wk-td-add" colSpan={weekDays.length + 2}>
                  <div className="wk-add-select">
                    <SearchableSelect
                      options={unplannedWorkers.map((w) => ({ value: String(w.id), label: w.name, badge: w.vog ? 'VOG' : null }))}
                      value=""
                      onChange={(v) => handleAddWorker(v)}
                      placeholder="＋ Medewerker toevoegen..."
                      searchPlaceholder="Zoek medewerker..."
                    />
                  </div>
                </td>
              </tr>
            )}

            {/* Quick-assign row — top */}
            <tr className="wk-quick-row wk-quick-row--sticky" style={{ '--wk-head-h': `${headH}px` }}>
              <td className="wk-td-name wk-quick-label">
                <span className="wk-name wk-quick-name">Snel inplannen</span>
                <span className="wk-skill">klik op een dag</span>
              </td>
              {weekDays.map((day, i) => (
                <td
                  key={i}
                  className={`wk-td-day wk-quick-cell ${isToday(day) ? 'wk-today' : ''}`}
                  onClick={() => openQuickModal(toDateStr(day))}
                >
                  <Plus size={13} className="wk-quick-plus" />
                </td>
              ))}
              <td className="wk-td-hours" />
            </tr>

            {orderedVisibleWorkers.map((worker, workerIdx) => {
              const wHours = getWorkerWeekHours(worker.id);
              const wDays = getWorkerWeekDays(worker.id);
              return (
                <tr key={worker.id} className="wk-row" data-worker-id={worker.id}>
                  <td className="wk-td-name">
                    <div className="wk-rank-wrap">
                      <input
                        className="wk-rank-num"
                        type="number"
                        min="1"
                        max={orderedVisibleWorkers.length}
                        defaultValue={workerIdx + 1}
                        key={`${worker.id}-${workerIdx}`}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.currentTarget.blur();
                          }
                        }}
                        onBlur={(e) => {
                          const val = parseInt(e.target.value);
                          if (!isNaN(val) && val !== workerIdx + 1) {
                            const ids = orderedVisibleWorkers.map((w) => w.id);
                            const newIds = ids.filter((id) => id !== worker.id);
                            const insertAt = Math.max(0, Math.min(val - 1, newIds.length));
                            newIds.splice(insertAt, 0, worker.id);
                            onSetWorkerOrder(newIds);
                          } else {
                            e.target.value = workerIdx + 1;
                          }
                        }}
                      />
                      <div className="wk-rank-arrows">
                        <button className="wk-rank-btn" onClick={(e) => { e.stopPropagation(); moveWorker(worker.id, -1); }} disabled={workerIdx === 0}>▲</button>
                        <button className="wk-rank-btn" onClick={(e) => { e.stopPropagation(); moveWorker(worker.id, 1); }} disabled={workerIdx === orderedVisibleWorkers.length - 1}>▼</button>
                      </div>
                    </div>
                    {skin === 'nieuw' && <span className="wk-avatar" aria-hidden="true">{initials(worker)}</span>}
                    <span className="wk-name wk-name-split">
                      <span>{worker.firstName || worker.name}</span>
                      {worker.lastName && <span>{worker.lastName}</span>}
                    </span>
                    {waInfo(worker.id).label && <span className={`wk-wa-status ${waInfo(worker.id).cls}`}>{waInfo(worker.id).label}</span>}
                    {worker.vog && <span className="wk-vog" title="VOG aanwezig">VOG</span>}
                    <span className="wk-skill">{worker.skill}</span>
                    {worker.opdrachtgeverName && <span className="wk-company">{worker.opdrachtgeverName}</span>}
                    <span className="wk-name-actions">
                    <button
                      className={`wk-wa-btn ${waInfo(worker.id).cls}`}
                      disabled={waBusyId === worker.id}
                      title={waInfo(worker.id).title || `Stuur rooster naar ${worker.name} via WhatsApp`}
                      onClick={(e) => { e.stopPropagation(); handleSendWhatsApp(worker); }}
                    >
                      {/* WhatsApp icon */}
                      {waBusyId === worker.id ? <Loader2 size={13} className="wk-spin" /> : <svg viewBox="0 0 24 24" fill="currentColor" width="13" height="13"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.117 1.528 5.845L0 24l6.335-1.502A11.954 11.954 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.017-1.374l-.36-.214-3.732.885.936-3.619-.234-.373A9.818 9.818 0 1112 21.818z"/></svg>}
                    </button>
                    <button
                      className="wk-share-btn"
                      title={`Deel rooster van ${worker.name} als afbeelding`}
                      onClick={(e) => { e.stopPropagation(); handleShareImage(worker); }}
                    >
                      <Share2 size={11} />
                    </button>
                    <button
                      className={`wk-mail-btn ${mailedIds.has(worker.id) ? 'mailed' : ''}`}
                      title={mailedIds.has(worker.id) ? 'Rooster al gemaild deze week — klik om opnieuw te sturen' : `Mail rooster naar ${worker.name}`}
                      disabled={mailBusyId === worker.id}
                      onClick={(e) => { e.stopPropagation(); handleMailWorker(worker); }}
                    >
                      {mailBusyId === worker.id ? <Loader2 size={11} className="wk-spin" /> : <Mail size={11} />}
                    </button>
                    <button
                      className="wk-del-btn"
                      title={`${worker.name} uit rooster verwijderen`}
                      onClick={(e) => { e.stopPropagation(); handleRemoveWorker(worker); }}
                    >
                      <X size={11} />
                    </button>
                    </span>
                  </td>
                  {weekDays.map((day, i) => {
                    const dateStr = toDateStr(day);
                    const cells = getScheduleForCell(worker.id, dateStr);
                    const hasSchedule = cells.length > 0;
                    return (
                      <td
                        key={i}
                        className={`wk-td-day ${hasSchedule ? 'wk-filled' : 'wk-empty'} ${isToday(day) ? 'wk-today' : ''}`}
                        onClick={() => openCellModal(worker.id, dateStr)}
                      >
                        {hasSchedule ? (
                          cells.map((s) => {
                            return (
                              <div key={s.id} className="wk-cell-text" style={{ '--blk': clientColor(s.client) }}>
                                <span className="wk-cell-time">{s.time}</span>
                                <span className="wk-cell-client">{s.client}</span>
                              </div>
                            );
                          })
                        ) : (
                          !notes[noteKey(worker.id, dateStr)] && <span className="wk-cell-empty">—</span>
                        )}
                        {notes[noteKey(worker.id, dateStr)] && (
                          <span className="wk-cell-note" title={notes[noteKey(worker.id, dateStr)]}>
                            {notes[noteKey(worker.id, dateStr)]}
                          </span>
                        )}
                      </td>
                    );
                  })}
                  <td className="wk-td-hours">
                    <span className="wk-hours-days">{wDays}d</span>
                    <span className="wk-hours-val">{wHours > 0 ? wHours.toFixed(1).replace('.', ',') : '0'}u</span>
                  </td>
                </tr>
              );
            })}

            {/* Add worker row */}
            {unplannedWorkers.length > 0 && (
              <tr className="wk-add-row">
                <td className="wk-td-add" colSpan={weekDays.length + 2}>
                  <div className="wk-add-select">
                    <SearchableSelect
                      options={unplannedWorkers.map((w) => ({ value: String(w.id), label: w.name, badge: w.vog ? 'VOG' : null }))}
                      value=""
                      onChange={(v) => handleAddWorker(v)}
                      placeholder="＋ Medewerker toevoegen..."
                      searchPlaceholder="Zoek medewerker..."
                    />
                  </div>
                </td>
              </tr>
            )}

            {/* Quick-assign row */}
            <tr className="wk-quick-row">
              <td className="wk-td-name wk-quick-label">
                <span className="wk-name wk-quick-name">Snel inplannen</span>
                <span className="wk-skill">klik op een dag</span>
              </td>
              {weekDays.map((day, i) => (
                <td
                  key={i}
                  className={`wk-td-day wk-quick-cell ${isToday(day) ? 'wk-today' : ''}`}
                  onClick={() => openQuickModal(toDateStr(day))}
                >
                  <Plus size={13} className="wk-quick-plus" />
                </td>
              ))}
              <td className="wk-td-hours" />
            </tr>
          </tbody>
          <tfoot>
            <tr className="wk-footer-row">
              <td className="wk-td-name wk-foot-label">Totaal</td>
              {weekDays.map((day, i) => {
                const dateStr = toDateStr(day);
                return (
                  <td key={i} className="wk-foot-cell">
                    <span className="wk-foot-count"><Users size={11} /> {getWorkerCountForDay(dateStr)}</span>
                    <span className="wk-foot-hours">{getDayTotalHours(dateStr).toFixed(1).replace('.', ',')}</span>
                  </td>
                );
              })}
              <td className="wk-foot-total">{totalWeekHours.toFixed(1).replace('.', ',')}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      </div>{/* end wk-nv-main */}
      {skin === 'nieuw' && (() => {
        const dates = weekDays.map(toDateStr);
        const weekA = assignments.filter((a) => dates.includes(a.date));
        const active = new Set(weekA.map((a) => a.workerId)).size;
        const hours = visibleWorkers.reduce((sum, w) => sum + (getWorkerWeekHours(w.id) || 0), 0);
        const weekNotes = Object.entries(notes).filter(([k]) => dates.includes(k.split('|')[1]));
        const sent = visibleWorkers.filter((w) => waInfo(w.id).cls === 'sent').length;
        const perDay = weekDays.map((d) => ({ d, n: weekA.filter((a) => a.date === toDateStr(d)).length }));
        const max = Math.max(1, ...perDay.map((x) => x.n));
        return (
          <aside className="wk-side">
            <section className="wk-side-card">
              <h4>Deze week</h4>
              <div className="wk-side-stat is-a"><b>{active}</b><span>actieve medewerkers</span></div>
              <div className="wk-side-stat is-b"><b>{weekA.length}</b><span>ingeplande diensten</span></div>
              <div className="wk-side-stat is-c"><b>{hours.toFixed(0)}</b><span>uur gepland</span></div>
              <div className="wk-side-stat is-d"><b>{sent}/{visibleWorkers.length}</b><span>rooster via WhatsApp</span></div>
            </section>
            <section className="wk-side-card">
              <h4>Bezetting per dag</h4>
              <ul className="wk-side-bars">
                {perDay.map(({ d, n }, i) => (
                  <li key={i} className={isToday(d) ? 'is-today' : ''}>
                    <span>{DAY_ABBR_NL[i]} {d.getDate()}</span>
                    <i><em style={{ width: `${(n / max) * 100}%` }} /></i>
                    <b>{n}</b>
                  </li>
                ))}
              </ul>
            </section>
            <section className="wk-side-card">
              <h4>Notities</h4>
              {weekNotes.length === 0 ? <p className="wk-side-empty">Geen notities deze week.</p> : (
                <ul className="wk-side-notes">
                  {weekNotes.slice(0, 12).map(([k, v]) => {
                    const [wid, ds] = k.split('|');
                    const w = workers.find((x) => String(x.id) === wid);
                    const d = new Date(ds + 'T00:00:00');
                    return (
                      <li key={k} onClick={() => openCellModal(Number(wid), ds)}>
                        <span>{w?.firstName || w?.name || '—'} · {d.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric' })}</span>
                        <em>{v}</em>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </aside>
        );
      })()}
      </div>{/* end wk-desktop-view */}

      {/* Quick-assign modal */}
      {quickCell && (() => {
        const freeWorkers = getWorkersNotOnDay(quickCell.dateStr);
        const dateLabel = new Date(quickCell.dateStr + 'T00:00:00').toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' });
        return (
          <div className="wk-overlay" onClick={() => setQuickCell(null)}>
            <div className="wk-modal" onClick={(e) => e.stopPropagation()}>
              <div className="wk-modal-head">
                <h3>Inplannen — {dateLabel}</h3>
                <button onClick={() => setQuickCell(null)}><X size={16} /></button>
              </div>
              <div className="wk-modal-form">
                {freeWorkers.length === 0 ? (
                  <p className="wk-no-workers">Alle medewerkers zijn al ingepland op deze dag.</p>
                ) : (
                  <>
                    <h4>Beschikbare medewerkers</h4>
                    <SearchableSelect
                      options={freeWorkers.map((w) => ({ value: String(w.id), label: w.name }))}
                      value={quickWorker}
                      onChange={(v) => {
                        setQuickWorker(v);
                        const wid = parseInt(v);
                        setQuickExtraDays((prev) => new Set([...prev].filter((ds) => !assignments.some((a) => a.workerId === wid && a.date === ds))));
                        setQuickError('');
                      }}
                      placeholder="Selecteer medewerker..."
                      searchPlaceholder="Zoek medewerker..."
                    />
                    <SearchableSelect
                      options={clients.map((c) => ({ value: c.name, label: c.name }))}
                      value={quickClient}
                      onChange={(v) => setQuickClient(v)}
                      placeholder="Selecteer locatie / klant..."
                      searchPlaceholder="Zoeken..."
                    />
                    {shifts.length > 0 && (
                      <SearchableSelect
                        options={shifts.map((s) => ({ value: String(s.id), label: `${s.name} (${s.startTime}–${s.endTime})` }))}
                        value=""
                        onChange={(v) => {
                          const s = shifts.find((sh) => sh.id === parseInt(v));
                          if (s) { setQuickTimeStart(s.startTime); setQuickTimeEnd(s.endTime); setQuickShiftId(s.id); }
                        }}
                        placeholder="Dienst selecteren..."
                        searchPlaceholder="Zoek dienst..."
                      />
                    )}
                    <div className="wk-time-row">
                      <input type="time" value={quickTimeStart} onChange={(e) => { setQuickTimeStart(e.target.value); setQuickError(''); }} />
                      <span>–</span>
                      <input type="time" value={quickTimeEnd} onChange={(e) => { setQuickTimeEnd(e.target.value); setQuickError(''); }} />
                    </div>
                    {(() => {
                      // Zonder medewerker: alle andere dagen; met medewerker: alleen dagen waarop hij nog vrij is.
                      const wid = quickWorker ? parseInt(quickWorker) : null;
                      const freeDays = weekDays.filter((day) => {
                        const ds = toDateStr(day);
                        return ds !== quickCell.dateStr && (!wid || !assignments.some((a) => a.workerId === wid && a.date === ds));
                      });
                      if (freeDays.length === 0) return null;
                      const allDs = freeDays.map(toDateStr);
                      const allChecked = allDs.every((ds) => quickExtraDays.has(ds));
                      return (
                        <div className="wk-extra-days">
                          <div className="wk-extra-label-row">
                            <span className="wk-extra-label">Ook inplannen op:</span>
                            <button type="button" className="wk-extra-all-btn"
                              onClick={() => { setQuickExtraDays(allChecked ? new Set() : new Set(allDs)); setQuickError(''); }}>
                              {allChecked ? 'Alles deselecteren' : 'Alle dagen'}
                            </button>
                          </div>
                          <div className="wk-extra-checks">
                            {freeDays.map((day) => {
                              const ds = toDateStr(day);
                              const checked = quickExtraDays.has(ds);
                              return (
                                <label key={ds} className={`wk-day-check ${checked ? 'checked' : ''}`}>
                                  <input type="checkbox" checked={checked}
                                    onChange={() => {
                                      setQuickExtraDays((prev) => {
                                        const next = new Set(prev);
                                        checked ? next.delete(ds) : next.add(ds);
                                        return next;
                                      });
                                      setQuickError('');
                                    }} />
                                  <span>{DAY_ABBR_NL[weekDays.indexOf(day)]} {day.getDate()}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
                    {quickError && <div className="wk-schedule-error" style={{ whiteSpace: 'pre-line' }}>{quickError}</div>}
                    <button className="wk-submit" onClick={handleQuickSchedule} disabled={!quickWorker || !quickClient}>
                      <Plus size={14} /> Inplannen{quickExtraDays.size > 0 ? ` (${1 + quickExtraDays.size} dagen)` : ''}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Cell modal */}
      {selectedCell && (
        <div className="wk-overlay" onClick={() => setSelectedCell(null)}>
          <div className="wk-modal" onClick={(e) => e.stopPropagation()}>
            <div className="wk-modal-head">
              <h3>
                {workers.find((w) => w.id === selectedCell.workerId)?.name} — {(() => {
                  const d = new Date(selectedCell.dateStr + 'T00:00:00');
                  return d.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' });
                })()}
              </h3>
              <button onClick={() => setSelectedCell(null)}><X size={16} /></button>
            </div>

            <div className="wk-modal-note">
              <h4>Notitie</h4>
              <div className="wk-note-row">
                <input
                  type="text"
                  value={noteDraft}
                  maxLength={500}
                  placeholder="bijv. ziek, vrij, belt terug…"
                  onChange={(e) => setNoteDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveNote(noteDraft); }}
                />
                <button type="button" className="wk-note-save" disabled={noteBusy || noteDraft.trim() === (notes[noteKey(selectedCell.workerId, selectedCell.dateStr)] || '')}
                  onClick={() => saveNote(noteDraft)}>
                  {noteBusy ? '…' : 'Opslaan'}
                </button>
                {notes[noteKey(selectedCell.workerId, selectedCell.dateStr)] && (
                  <button type="button" className="wk-note-del" disabled={noteBusy} onClick={() => saveNote('')} title="Notitie verwijderen">
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>

            {getScheduleForCell(selectedCell.workerId, selectedCell.dateStr).length > 0 && (
              <div className="wk-modal-existing">
                <h4>Ingepland</h4>
                {getScheduleForCell(selectedCell.workerId, selectedCell.dateStr).map((s) => (
                  <div key={s.id} className={`wk-exist-row ${editingAssignment?.id === s.id ? 'wk-exist-row-editing' : ''}`}>
                    <span>{s.time}</span>
                    <span>{s.client}</span>
                    <button
                      className="wk-edit-btn"
                      title="Wijzig inplanning"
                      onClick={() => {
                        const [ts, te] = s.time.split(' - ');
                        setEditingAssignment(s);
                        setEditingDayIds(new Set([s.id]));
                        setScheduleTimeStart(ts?.trim() || '08:30');
                        setScheduleTimeEnd(te?.trim() || '17:00');
                        setScheduleClient(s.client);
                        setScheduleExtraDays(new Set());
                        setCellError('');
                      }}
                    >
                      <Pencil size={13} />
                    </button>
                    <button onClick={() => handleDeleteSchedule(s.id)}><X size={13} /></button>
                  </div>
                ))}
              </div>
            )}

            {editingAssignment ? (
              <div className="wk-modal-form">
                <div className="wk-edit-header">
                  <h4>Wijzig inplanning</h4>
                  <button className="wk-cancel-edit-btn" onClick={() => { setEditingAssignment(null); setEditingDayIds(new Set()); setCellError(''); }}>Annuleren</button>
                </div>
                {/* Multi-day selector: all days this worker has assignments this week */}
                {(() => {
                  const workerWeekAssignments = weekDays.flatMap((day, i) =>
                    getScheduleForCell(editingAssignment.workerId, toDateStr(day)).map((a) => ({ a, day, i }))
                  );
                  if (workerWeekAssignments.length <= 1) return null;
                  const allIds = workerWeekAssignments.map(({ a }) => a.id);
                  const allChecked = allIds.every((id) => editingDayIds.has(id));
                  return (
                    <div className="wk-extra-days">
                      <div className="wk-extra-label-row">
                        <span className="wk-extra-label">Toepassen op:</span>
                        <button type="button" className="wk-extra-all-btn" onClick={() => {
                          setEditingDayIds(allChecked ? new Set([editingAssignment.id]) : new Set(allIds));
                        }}>
                          {allChecked ? 'Alles deselecteren' : 'Alle dagen'}
                        </button>
                      </div>
                      <div className="wk-extra-checks">
                        {workerWeekAssignments.map(({ a, day, i }) => {
                          const checked = editingDayIds.has(a.id);
                          return (
                            <label key={a.id} className={`wk-day-check ${checked ? 'checked' : ''}`}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => {
                                  setEditingDayIds((prev) => {
                                    const next = new Set(prev);
                                    checked ? next.delete(a.id) : next.add(a.id);
                                    return next;
                                  });
                                  setCellError('');
                                }}
                              />
                              <span>{DAY_ABBR_NL[i]} {day.getDate()}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
                <SearchableSelect
                  options={clients.map((c) => ({ value: c.name, label: c.name }))}
                  value={scheduleClient}
                  onChange={(v) => { setScheduleClient(v); setCellError(''); }}
                  placeholder="Selecteer klant..."
                  searchPlaceholder="Zoeken..."
                />
                {shifts.length > 0 && (
                  <SearchableSelect
                    options={shifts.map((s) => ({ value: String(s.id), label: `${s.name} (${s.startTime}–${s.endTime})` }))}
                    value=""
                    onChange={(v) => {
                      const s = shifts.find((sh) => sh.id === parseInt(v));
                      if (s) { setScheduleTimeStart(s.startTime); setScheduleTimeEnd(s.endTime); setScheduleShiftId(s.id); }
                    }}
                    placeholder="Dienst selecteren..."
                    searchPlaceholder="Zoek dienst..."
                  />
                )}
                <div className="wk-time-row">
                  <input type="time" value={scheduleTimeStart} onChange={(e) => { setScheduleTimeStart(e.target.value); setCellError(''); }} />
                  <span>–</span>
                  <input type="time" value={scheduleTimeEnd} onChange={(e) => { setScheduleTimeEnd(e.target.value); setCellError(''); }} />
                </div>
                {cellError && <div className="wk-schedule-error" style={{ whiteSpace: 'pre-line' }}>{cellError}</div>}
                <button className="wk-submit" onClick={handleEditSchedule} disabled={!scheduleClient || editingDayIds.size === 0}>
                  Opslaan{editingDayIds.size > 1 ? ` (${editingDayIds.size} dagen)` : ''}
                </button>
              </div>
            ) : (
              <div className="wk-modal-form">
                <h4>Nieuwe inplanning</h4>
                <SearchableSelect
                  options={clients.map((c) => ({ value: c.name, label: c.name }))}
                  value={scheduleClient}
                  onChange={(v) => setScheduleClient(v)}
                  placeholder="Selecteer klant..."
                  searchPlaceholder="Zoeken..."
                />

                {shifts.length > 0 && (
                  <SearchableSelect
                    options={shifts.map((s) => ({ value: String(s.id), label: `${s.name} (${s.startTime}–${s.endTime})` }))}
                    value=""
                    onChange={(v) => {
                      const s = shifts.find((sh) => sh.id === parseInt(v));
                      if (s) { setScheduleTimeStart(s.startTime); setScheduleTimeEnd(s.endTime); setScheduleShiftId(s.id); }
                    }}
                    placeholder="Dienst selecteren..."
                    searchPlaceholder="Zoek dienst..."
                  />
                )}

                <div className="wk-time-row">
                  <input type="time" value={scheduleTimeStart} onChange={(e) => { setScheduleTimeStart(e.target.value); setCellError(''); }} />
                  <span>–</span>
                  <input type="time" value={scheduleTimeEnd} onChange={(e) => { setScheduleTimeEnd(e.target.value); setCellError(''); }} />
                </div>

                {(() => {
                  const freeDays = weekDays.filter((day) => {
                    const ds = toDateStr(day);
                    return ds !== selectedCell.dateStr && !assignments.some((a) => a.workerId === selectedCell.workerId && a.date === ds);
                  });
                  if (freeDays.length === 0) return null;
                  return (
                    <div className="wk-extra-days">
                      <div className="wk-extra-label-row">
                        <span className="wk-extra-label">Ook inplannen op:</span>
                        <button
                          type="button"
                          className="wk-extra-all-btn"
                          onClick={() => {
                            const allDs = freeDays.map(toDateStr);
                            const allChecked = allDs.every((ds) => scheduleExtraDays.has(ds));
                            setScheduleExtraDays(allChecked ? new Set() : new Set(allDs));
                          }}
                        >
                          {freeDays.every((d) => scheduleExtraDays.has(toDateStr(d))) ? 'Alles deselecteren' : 'Alle dagen'}
                        </button>
                      </div>
                      <div className="wk-extra-checks">
                        {freeDays.map((day, i) => {
                          const ds = toDateStr(day);
                          const checked = scheduleExtraDays.has(ds);
                          const dayIdx = weekDays.indexOf(day);
                          return (
                            <label key={ds} className={`wk-day-check ${checked ? 'checked' : ''}`}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => {
                                  setScheduleExtraDays((prev) => {
                                    const next = new Set(prev);
                                    checked ? next.delete(ds) : next.add(ds);
                                    return next;
                                  });
                                }}
                              />
                              <span>{DAY_ABBR_NL[dayIdx]} {day.getDate()}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {cellError && <div className="wk-schedule-error" style={{ whiteSpace: 'pre-line' }}>{cellError}</div>}
                <button className="wk-submit" onClick={handleCellSchedule} disabled={!scheduleClient}>
                  <Plus size={14} /> Inplannen{scheduleExtraDays.size > 0 ? ` (${1 + scheduleExtraDays.size} dagen)` : ''}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Copy previous week modal */}
      {copyWeekModal && (() => {
        // Group items by workerId
        const workerGroups = [];
        const seen = {};
        for (const item of copyWeekModal.items) {
          if (!seen[item.workerId]) {
            seen[item.workerId] = true;
            workerGroups.push({ workerId: item.workerId, workerName: item.workerName });
          }
        }
        const toggleWorker = (wid) => {
          const workerItems = copyWeekModal.items.filter((i) => i.workerId === wid && !i.conflict);
          const allSelected = workerItems.every((i) => i.selected);
          setCopyWeekModal((prev) => ({
            ...prev,
            items: prev.items.map((it) =>
              it.workerId === wid && !it.conflict ? { ...it, selected: !allSelected } : it
            ),
          }));
        };
        const toggleItem = (idx) => {
          setCopyWeekModal((prev) => ({
            ...prev,
            items: prev.items.map((it, i) => i === idx ? { ...it, selected: !it.selected } : it),
          }));
        };
        const selectedCount = copyWeekModal.items.filter((i) => i.selected && !i.conflict).length;
        return (
          <div className="wk-overlay" onClick={() => setCopyWeekModal(null)}>
            <div className="wk-modal wk-copy-modal" onClick={(e) => e.stopPropagation()}>
              <div className="wk-modal-head">
                <h3>Rooster week {copyWeekModal.prevWeekNum} kopiëren naar week {weekNumber}</h3>
                <button onClick={() => setCopyWeekModal(null)}><X size={16} /></button>
              </div>
              <div className="wk-copy-body">
                {copyWeekModal.items.some((i) => i.conflict) && (
                  <p className="wk-copy-info"><span className="wk-copy-conflict-note">Conflicten zijn uitgevinkt</span> — deze medewerkers staan al ingepland op die dag.</p>
                )}
                <div className="wk-copy-list">
                  {workerGroups.map(({ workerId: wid, workerName }) => {
                    const workerItems = copyWeekModal.items.filter((i) => i.workerId === wid);
                    const nonConflict = workerItems.filter((i) => !i.conflict);
                    const allChecked = nonConflict.length > 0 && nonConflict.every((i) => i.selected);
                    const someChecked = nonConflict.some((i) => i.selected);
                    return (
                      <div key={wid} className="wk-copy-worker-group">
                        <label className="wk-copy-worker-header">
                          <input
                            type="checkbox"
                            checked={allChecked}
                            ref={(el) => { if (el) el.indeterminate = !allChecked && someChecked; }}
                            disabled={nonConflict.length === 0}
                            onChange={() => toggleWorker(wid)}
                          />
                          <span className="wk-copy-worker-name">{workerName}</span>
                          <span className="wk-copy-worker-count">{nonConflict.filter((i) => i.selected).length}/{workerItems.length} dienst{workerItems.length !== 1 ? 'en' : ''}</span>
                        </label>
                        <div className="wk-copy-worker-items">
                          {workerItems.map((item, idx) => {
                            const globalIdx = copyWeekModal.items.indexOf(item);
                            const newDateLabel = new Date(item.newDate + 'T00:00:00').toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'short' });
                            return (
                              <label
                                key={globalIdx}
                                className={`wk-copy-item ${item.conflict ? 'wk-copy-conflict' : ''} ${item.selected ? 'wk-copy-checked' : ''}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={item.selected}
                                  disabled={!!item.conflict}
                                  onChange={() => toggleItem(globalIdx)}
                                />
                                <div className="wk-copy-item-info">
                                  <span className="wk-copy-details">
                                    {newDateLabel} · {item.assignment.time} · {item.assignment.client}
                                  </span>
                                  {item.conflict && (
                                    <span className="wk-copy-conflict-label">Al ingepland op deze dag</span>
                                  )}
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="wk-copy-footer">
                  <span className="wk-copy-count">{selectedCount} van {copyWeekModal.items.length} diensten geselecteerd</span>
                  <div className="wk-copy-actions">
                    <button className="wk-cancel-btn" onClick={() => setCopyWeekModal(null)}>Annuleren</button>
                    <button className="wk-submit" onClick={executeCopyWeek} disabled={selectedCount === 0}>
                      <Plus size={14} /> Kopiëren
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
